import React from "react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../shared/chartAreaFill";
import { formatAnalyticsDayLabel } from "../shared/analyticsFormatting";
import { RevenueChartLegend } from "../revenue/RevenueChartLegend";
import { REVENUE_STACKED_AREA_CHART_HEIGHT } from "../revenue/constants";
import type {
  OperationsChartSeries,
  OperationsExecutionTrendPoint,
} from "./types";

const AVG_TIME_AXIS_ID = "avgTime";
const ON_TIME_AXIS_ID = "onTimePercent";
const ON_TIME_AXIS_TICKS = [0, 20, 40, 60, 80, 100];
const AVG_TIME_AXIS_MIN_MAX = 20;

interface OperationsExecutionChartProps {
  data: OperationsExecutionTrendPoint[];
  series: OperationsChartSeries[];
  showLegend?: boolean;
  chartHeight?: number;
}

function formatMinutes(value: number): string {
  return `${Number(value.toFixed(1))}m`;
}

function formatPercent(value: number): string {
  return `${Number(value.toFixed(1))}%`;
}

function ExecutionTooltip({
  active,
  payload,
  label,
  series,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string; value?: number }>;
  label?: string;
  series: OperationsChartSeries[];
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const seriesByKey = Object.fromEntries(
    series.map((item) => [item.key, item]),
  );

  return (
    <div className="border-border/50 bg-background grid min-w-[10rem] gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <p className="font-medium text-zinc-900">
        {formatAnalyticsDayLabel(label)}
      </p>
      {payload.map((item) => {
        const config = seriesByKey[String(item.dataKey)];
        if (!config) {
          return null;
        }

        const value = Number(item.value ?? 0);
        const formatted =
          config.key === ON_TIME_AXIS_ID
            ? formatPercent(value)
            : formatMinutes(value);

        return (
          <div
            key={config.key}
            className="flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: config.color }}
              />
              <span className="text-muted-foreground">{config.label}</span>
            </div>
            <span className="font-medium text-zinc-900">{formatted}</span>
          </div>
        );
      })}
    </div>
  );
}

export const OperationsExecutionChart: React.FC<
  OperationsExecutionChartProps
> = ({
  data,
  series,
  showLegend = false,
  chartHeight = REVENUE_STACKED_AREA_CHART_HEIGHT,
}) => {
  const { t } = useTranslation();
  const chartInstanceId = useAnalyticsChartInstanceId();

  const chartConfig = series.reduce<ChartConfig>((config, item) => {
    config[item.key] = { label: item.label, color: item.color };
    return config;
  }, {});

  const maxAvgTime = data.reduce(
    (max, point) => Math.max(max, Number(point.avgTime ?? 0)),
    0,
  );
  const avgTimeMax = Math.max(
    AVG_TIME_AXIS_MIN_MAX,
    Math.ceil(maxAvgTime / 5) * 5,
  );
  const avgTimeTicks = Array.from(
    { length: avgTimeMax / 5 + 1 },
    (_, index) => index * 5,
  );
  // Widen the axis gutter so longer (3+ digit) minute labels aren't clipped.
  const avgTimeAxisWidth = Math.max(40, String(avgTimeMax).length * 10 + 16);

  return (
    <div className="flex flex-col gap-3">
      <ChartContainer
        config={chartConfig}
        className="!aspect-auto w-full"
        style={{ height: chartHeight }}
      >
        <AreaChart
          data={data}
          margin={{ top: 8, right: 0, left: 0, bottom: 0 }}
        >
          <defs>
            <AnalyticsAreaFillDefs
              seriesKeys={series.map((item) => item.key)}
              chartInstanceId={chartInstanceId}
            />
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            interval={0}
            className="text-muted-foreground text-xs"
          />
          <YAxis
            yAxisId={AVG_TIME_AXIS_ID}
            orientation="left"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={avgTimeAxisWidth}
            domain={[0, avgTimeMax]}
            ticks={avgTimeTicks}
            className="text-muted-foreground text-xs"
          />
          <YAxis
            yAxisId={ON_TIME_AXIS_ID}
            orientation="right"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={40}
            domain={[0, 100]}
            ticks={ON_TIME_AXIS_TICKS}
            className="text-muted-foreground text-xs"
          />
          <ChartTooltip
            cursor={false}
            content={<ExecutionTooltip series={series} />}
          />
          <Area
            yAxisId={ON_TIME_AXIS_ID}
            type="monotone"
            dataKey={ON_TIME_AXIS_ID}
            stroke={`var(--color-${ON_TIME_AXIS_ID})`}
            fill={analyticsAreaFillUrl(ON_TIME_AXIS_ID, chartInstanceId)}
            strokeWidth={1.5}
          />
          <Area
            yAxisId={AVG_TIME_AXIS_ID}
            type="monotone"
            dataKey={AVG_TIME_AXIS_ID}
            stroke={`var(--color-${AVG_TIME_AXIS_ID})`}
            fill={analyticsAreaFillUrl(AVG_TIME_AXIS_ID, chartInstanceId)}
            strokeWidth={1.5}
          />
        </AreaChart>
      </ChartContainer>
      <div className="flex shrink-0 items-center justify-between text-xs leading-normal font-medium text-[#737373]">
        <span>{t("analytics.operations.axis.timeMin", "Time, min")}</span>
        <span>{t("analytics.operations.axis.percent", "%")}</span>
      </div>
      {showLegend ? (
        <div className="shrink-0">
          <RevenueChartLegend items={series} />
        </div>
      ) : null}
    </div>
  );
};
